import { onMounted, onUnmounted, reactive, shallowRef, toRefs, watchEffect } from 'vue'
import type { Ref } from 'vue'

export const breakpoints = ['sm', 'md', 'lg', 'xl'] as const // no xs

export type Breakpoint = typeof breakpoints[number]

export type DisplayBreakpoint = 'xs' | Breakpoint

export type DisplayThresholds = {
  [key in DisplayBreakpoint]: number
}

export interface DisplayInstance {
  xs: Ref<boolean>
  sm: Ref<boolean>
  md: Ref<boolean>
  lg: Ref<boolean>
  xl: Ref<boolean>
  xxl: Ref<boolean>
  smAndUp: Ref<boolean>
  mdAndUp: Ref<boolean>
  lgAndUp: Ref<boolean>
  xlAndUp: Ref<boolean>
  smAndDown: Ref<boolean>
  mdAndDown: Ref<boolean>
  lgAndDown: Ref<boolean>
  xlAndDown: Ref<boolean>
  name: Ref<DisplayBreakpoint>
  height: Ref<number>
  width: Ref<number>
  mobile: Ref<boolean>
  mobileBreakpoint: Ref<number | DisplayBreakpoint>
  thresholds: Ref<DisplayThresholds>
}

const display = reactive({} as DisplayInstance)
let displayCreated = false

function createDisplay(): void {
  const mobileBreakpoint = 'md'
  const thresholds = {
    xs: 0,
    sm: 567,
    md: 768,
    lg: 992,
    xl: 1200,
  }

  const height = shallowRef(window.innerHeight)
  const width = shallowRef(window.innerWidth)

  const updateSize = () => {
    width.value = window.innerWidth;
    height.value = window.innerHeight;
  }

  onMounted(() => {
    window.addEventListener('resize', updateSize);
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateSize);
  })

  watchEffect(() => {
    const xs = width.value < thresholds.sm
    const sm = width.value < thresholds.md && !xs
    const md = width.value < thresholds.lg && !(sm || xs)
    const lg = width.value < thresholds.xl && !(md || sm || xs)
    const xl = width.value >= thresholds.xl
    const name =
      xs ? 'xs'
      : sm ? 'sm'
      : md ? 'md'
      : lg ? 'lg'
      : 'xl'
    const breakpointValue = typeof mobileBreakpoint === 'number' ? mobileBreakpoint : thresholds[mobileBreakpoint]
    const mobile = width.value < breakpointValue

    display.xs = xs
    display.sm = sm
    display.md = md
    display.lg = lg
    display.xl = xl
    display.smAndUp = !xs
    display.mdAndUp = !(xs || sm)
    display.lgAndUp = !(xs || sm || md)
    display.xlAndUp = !(xs || sm || md || lg)
    display.smAndDown = !(md || lg || xl)
    display.mdAndDown = !(lg || xl)
    display.lgAndDown = !xl
    display.name = name
    display.height = height.value
    display.width = width.value
    display.mobile = mobile
    display.mobileBreakpoint = mobileBreakpoint
    display.thresholds = thresholds
  })

  displayCreated = true
}

export function useDisplay(): DisplayInstance {
  if(!displayCreated) {
    createDisplay()
  }
  return { ...toRefs(display) }
}
